import { UserResponse, UserCustomerType } from '../User/contracts';
import { ProductResponse } from '../Product/contracts';

/**
 * Defines the possible statuses for an order.
 * Using an enum provides both the type and a runtime value for comparisons.
 */
export enum OrderStatus {
  PENDING = 'pending',
  ACCEPTED = 'accepted',
  CLIENT_ACCEPTED = 'client-accepted',
  CLIENT_DECLINED = 'client-declined',
  RENTED = 'rented',
  RETURNED = 'returned',
  DECLINED = 'declined'
}

/**
 * Defines the types for downloadable documents.
 * The backend expects snake_case, so we assign those values here.
 */
export enum DocumentDownloadType {
  ACCEPTED_ORDER = 'accepted_order',
  DECLINED_ORDER = 'declined_order',
  RENTED_ORDER = 'rented_order',
  RETURNED_ORDER = 'returned_order'
}

/**
 * The payload required to create a new order via the API.
 * This is used by the non-logged-in user checkout form.
 */
export interface OrderCreatePayload {
  cart_token: string;
  full_name: string;
  type: UserCustomerType;
  personal_id_number: string;
  company_name: string;
  company_tax_number: number | string;
  company_id_number: number | string;
  email: string;
  phone: string;
  state: string;
  city: string;
  address: string;
  zip: number | string;
  note: string;
  washing_fee: boolean;
  reservation_start: Date | string;
  reservation_end: Date | string;
}

/**
 * The payload for updating an order's status.
 */
export interface OrderUpdatePayload {
  status: OrderStatus;
}

/**
 * Defines the shape of a single product within an order response,
 * including the pivot data like quantity and price at the time of order.
 */
export interface OrderProduct extends ProductResponse {
  pivot: {
    order_id: number;
    product_id: number;
    quantity: number;
    price: number;
  };
}

/**
 * Defines the full API response for a single order object.
 */
export interface OrderResponse {
  readonly id: string;
  readonly number_of_order: string;
  readonly status: OrderStatus;
  readonly note: string | null;
  readonly full_name: string;
  readonly type: 'individual' | 'company';
  readonly personal_id_number?: string;
  readonly company_name: string | null;
  readonly company_tax_number: string | null;
  readonly company_id_number: string | null;
  readonly email: string;
  readonly phone: string;
  readonly state: string;
  readonly city: string;
  readonly address: string;
  readonly zip: string;
  readonly reservation_start: string;
  readonly reservation_end: string;
  readonly washing_fee: boolean;
  readonly total_price: number;
  readonly price_factor: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly user?: UserResponse;
  readonly products: OrderProduct[];
  readonly washing_fee_percentage: number;
  readonly washing_fee_value: number;
  readonly tax_rate: number;
  readonly tax_value: number;
}

/**
 * Query parameters for fetching a list of orders.
 */
export interface OrderQueryParams {
  page?: number;
  per_page?: number;
  status?: string;
  search?: string;
}

/**
 * Defines the shape of a single document within an order response
 */
export interface DocumentOrder {
  id: number;
  name: string;
  original_name: string; 
  filename: string;
  path: string;
  type: string;
  url: string;
  created_at: string;
  updated_at: string;
}
/**
 * Interface for the raw paginated response from the Laravel API.
 */
export interface LaravelPaginatedResponse {
  data: OrderResponse[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

/**
 * Interface for the response from the manual admin order creation endpoint.
 * This should match what the backend's Admin/OrderController@create method returns.
 */
export interface ManualOrderCreateResponse {
  message: string;
  order: {
    id: string;
    number_of_order: string;
  };
}
